import React, { useState } from 'react';
import { Checkbox, FormControlLabel, Grid, Stack, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { useSelector } from 'react-redux';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { ADMIN_MODE_FEATURE_FLAG } from '@/hooks/useAdminModeAccess';
import { useListAllFeatureFlagsQuery } from '@/admin/featureFlags/api/featureFlagsApi';
import { AdminFeatureFlagPicker } from '@/admin/shared/pickers/AdminFeatureFlagPicker';
import { FeatureFlagChip, sortFeatureFlagChips } from '@/admin/shared/chips/FeatureFlagChip';
import { UndoFieldButton } from '@/admin/shared/UndoFieldButton';

/**
 * The user edit drawer's Feature flags section: the flags that are on for this user as
 * chips, then a picker for the ones that target them directly. The form's `featureFlags`
 * (flag ids) is the draft; Save adds or removes the user on each flag that changed.
 *
 * - Chips that target the user (the draft) can be removed with their ×. Flags on through
 *   one of their broker groups ("Include broker groups"), their ledger ("Include ledger") and
 *   everyone ("Include global"), all off to start, show when ticked, in their own colours,
 *   and can't be removed here.
 * - Admins can't take themselves off adminModeEnabled (see useFeatureFlagRules).
 *
 * brokerGroupIds and ledger are the user's saved ones (for a new user, the ones chosen so far).
 */
export const FeatureFlagFields = ({ userId, brokerGroupIds, ledger }) => {
	const { values, initialValues, setFieldValue } = useFormikContext();
	const { data: flags = [] } = useListAllFeatureFlagsQuery();
	const email = useSelector((state) => state.msalAccount.account?.username);
	const { data: currentUser } = useGetUserDetailsQuery({ email }, { skip: !email });
	const [includeBrokerGroups, setIncludeBrokerGroups] = useState(false);
	const [includeLedger, setIncludeLedger] = useState(false);
	const [includeGlobal, setIncludeGlobal] = useState(false);

	const isLocked = (flag) =>
		flag?.feature === ADMIN_MODE_FEATURE_FLAG && currentUser?.userId === userId;
	const getSource = (flag) =>
		(values.featureFlags.includes(flag.id) && 'direct') ||
		(includeBrokerGroups &&
			(flag.brokerGroups ?? []).some((id) => brokerGroupIds.includes(id)) &&
			'brokerGroup') ||
		(includeLedger && ledger && (flag.ledgers ?? []).includes(ledger) && 'ledger') ||
		(includeGlobal && flag.active && 'global');

	const chips = sortFeatureFlagChips(
		flags.map((flag) => ({ flag, source: getSource(flag) })).filter(({ source }) => source),
	);

	const removeFlag = (flagId) =>
		setFieldValue(
			'featureFlags',
			values.featureFlags.filter((id) => id !== flagId),
		);

	return (
		<>
			<Grid size={12}>
				<Typography variant='form_label'>Feature flags</Typography>
			</Grid>
			<Grid size={12}>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeBrokerGroups}
							onChange={(event) => setIncludeBrokerGroups(event.target.checked)}
						/>
					}
					label='Include broker groups'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeLedger}
							onChange={(event) => setIncludeLedger(event.target.checked)}
						/>
					}
					label='Include ledger'
				/>
				<FormControlLabel
					control={
						<Checkbox
							checked={includeGlobal}
							onChange={(event) => setIncludeGlobal(event.target.checked)}
						/>
					}
					label='Include global'
				/>
				<Stack direction='row' flexWrap='wrap' gap={0.5} alignItems='center'>
					{chips.length ? (
						chips.map(({ flag, source }) => (
							<FeatureFlagChip
								key={flag.id}
								flag={flag}
								source={source}
								{...(source === 'direct' &&
									!isLocked(flag) && { onDelete: () => removeFlag(flag.id) })}
							/>
						))
					) : (
						<Typography variant='body2' color='text.secondary'>
							No feature flags to show
						</Typography>
					)}
					{/* Puts back the saved flags; the picker can't clear them all at once */}
					<UndoFieldButton name='featureFlags' />
				</Stack>
			</Grid>
			<Grid size={12}>
				<AdminFeatureFlagPicker
					brokerGroupIds={brokerGroupIds}
					ledger={ledger}
					value={values.featureFlags}
					onChange={(ids) => setFieldValue('featureFlags', ids)}
					// Flags are removed one at a time; the heading's undo puts back the saved ones
					disableClearable
					showCheckbox
					isOptionFixed={(option) =>
						initialValues.featureFlags.includes(option.id) && isLocked(option.flag)
					}
					// The chips above show the selection, so the input doesn't repeat them
					renderValue={() => null}
				/>
			</Grid>
		</>
	);
};
